import { useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { playNarration, stopNarration, splitSegments } from "../../lib/narration";

const readingMs = (text) => Math.max(3500, text.split(/\s+/).length * 380);

// Game 1 formatting: brand bar, centred kicker + headline, Scientist subtitle band along the bottom.
// Standby -> (Begin) -> the Scientist reads the briefing paragraph by paragraph -> onFinish.
export const IntroOverlay = ({ open, phase, title, total, briefing, onBegin, onFinish }) => {
  const segments = splitSegments(briefing);
  const [seg, setSeg] = useState(0);
  const finish = useRef(onFinish);
  finish.current = onFinish;

  useEffect(() => {
    if (!open || phase !== "briefing") { setSeg(0); return undefined; }
    let cancelled = false;
    let timer = null;
    const run = async (i) => {
      if (cancelled) return;
      if (i >= segments.length) { finish.current(); return; }
      setSeg(i);
      const ok = await playNarration("briefing", { seg: i });
      if (cancelled) return;
      if (ok) run(i + 1);
      else timer = setTimeout(() => run(i + 1), readingMs(segments[i]));
    };
    run(0);
    return () => { cancelled = true; clearTimeout(timer); stopNarration(); };
  }, [open, phase, briefing]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;
  const speaking = phase === "briefing";
  return (
    <div data-testid="intro-overlay" data-phase={phase} className="fixed inset-0 z-50 flex flex-col bg-[#050807]/97 backdrop-blur-xl">
      <div className="spore-field" />
      <header className="relative flex items-center justify-between gap-4 px-6 py-5 sm:px-10">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="brand text-lg sm:text-xl">The Spore Project</span>
          <span className="lab-label hidden sm:inline">Research &amp; Containment Site · 123 Grey's Inn Road, London</span>
        </div>
        <span data-testid="intro-status" className="lab-label !text-emerald-300">{speaking ? "Briefing in progress" : "Awaiting researchers"}</span>
      </header>

      <main className="relative flex flex-1 flex-col items-center justify-center px-6 text-center">
        <p className="lab-label unlock-in" style={{ animationDelay: "0.1s" }}>Spore Lab</p>
        <h1 data-testid="intro-title" className={`spore-headline mt-3 unlock-in transition-all duration-700 ${speaking ? "text-4xl sm:text-5xl lg:text-6xl" : "text-6xl sm:text-8xl lg:text-9xl"}`} style={{ animationDelay: "0.3s" }}>
          {title}
        </h1>
        {!speaking && (
          <div className="mt-12 flex flex-col items-center gap-5 unlock-in" style={{ animationDelay: "0.7s" }}>
            <button data-testid="intro-begin-button" onClick={onBegin} className="lab-btn-primary px-10 py-4 text-xl">Begin research</button>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
              Enter to begin · ` menu · specimens on site: <span data-testid="intro-total">{total}</span>
            </span>
          </div>
        )}
      </main>

      {speaking && (
        <footer className="relative border-t border-amber-300/40 bg-[#050807]/90 px-6 py-8 sm:px-12">
          <div className="mx-auto max-w-5xl text-center">
            <p className="scientist-label flex items-center justify-center gap-2">
              <Mic size={13} className="animate-pulse" /> The Scientist · {seg + 1}/{segments.length}
            </p>
            <p key={seg} data-testid="intro-briefing" className="scientist-line note-in mt-4 text-xl sm:text-2xl lg:text-3xl leading-snug">
              {segments[seg]}
            </p>
            <div className="mt-6 flex items-center justify-center gap-4">
              <span className="flex gap-1.5">
                {segments.map((_, i) => <i key={i} className={`block h-1.5 w-8 ${i <= seg ? "bg-amber-300" : "bg-slate-700"}`} />)}
              </span>
              <button data-testid="intro-skip-button" onClick={onFinish} className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 hover:text-slate-200 transition-colors">
                Enter to skip · or place the first object on the pad
              </button>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};
